/**
 * ReportMetadataContext — shares parsed report-metadata between
 * ReportViewer (producer) and ChatPanel (consumer).
 *
 * When a workspace report is selected and contains a
 * <script id="report-metadata"> JSON block, the metadata is parsed
 * and exposed here. ChatPanel uses it to:
 *   1. Attach as context in AI requests
 *   2. Display suggested_questions as clickable chips
 */

import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';

// ── Types ───────────────────────────────────────────────────────────────────

export interface ReportMetadata {
  data_window?: {
    cw_start: string;
    cw_end: string;
    pw_start: string;
    pw_end: string;
    cw_days: number;
  };
  kpi?: Record<string, number>;
  top_accelerators?: Array<Record<string, unknown>>;
  top_decelerators?: Array<Record<string, unknown>>;
  highlights?: string[];
  suggested_questions?: string[];
  template?: string;
  sub_segments?: string[];
  [key: string]: unknown;
}

interface ReportMetadataContextValue {
  /** Parsed metadata from the currently selected report (null if none) */
  metadata: ReportMetadata | null;
  /** Update metadata when a report is loaded */
  setMetadata: (m: ReportMetadata | null) => void;
}

// ── Context ─────────────────────────────────────────────────────────────────

const ReportMetadataContext = createContext<ReportMetadataContextValue>({
  metadata: null,
  setMetadata: () => {},
});

export function ReportMetadataProvider({ children }: { children: ReactNode }) {
  const [metadata, setMetadataState] = useState<ReportMetadata | null>(null);

  const setMetadata = useCallback((m: ReportMetadata | null) => {
    setMetadataState(m);
  }, []);

  const value = useMemo(() => ({ metadata, setMetadata }), [metadata, setMetadata]);

  return (
    <ReportMetadataContext.Provider value={value}>
      {children}
    </ReportMetadataContext.Provider>
  );
}

export function useReportMetadata() {
  return useContext(ReportMetadataContext);
}

// ── Helper: parse report-metadata from HTML string ──────────────────────────

/**
 * Extract and parse the <script id="report-metadata"> JSON block from
 * an HTML string. Returns null if not found or invalid JSON.
 */
export function parseReportMetadata(html: string): ReportMetadata | null {
  try {
    // Use regex to extract the script content — avoids DOM parsing overhead
    const match = html.match(
      /<script[^>]+id=["']report-metadata["'][^>]*>([\s\S]*?)<\/script>/i
    );
    if (!match || !match[1]) return null;
    const parsed = JSON.parse(match[1].trim());
    return parsed as ReportMetadata;
  } catch {
    return null;
  }
}
